import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import PasswordInput from "../Auth/passwordInput";
import PlaceTrainerSelect from "../Auth/placeTrainerSelect";
import TwoFactor from "./twoFactor";
import { Dots } from "../Loader/loader";

const TODAY = new Date().toISOString().slice(0, 10);

const Settings = () => {
  const { user, updateProfile, updatePhoto, changePassword } = useAuth();

  const [form, setForm] = useState({
    firstname: user.firstname || "",
    surname: user.surname || "",
    patronymic: user.patronymic || "",
    date_of_birth: user.date_of_birth || "",
    sex: user.sex || "",
    address: user.address || "",
    phone: user.phone || "",
    email: user.email || "",
    place_id: user.place ? String(user.place.id) : "",
    trainer_id: user.trainer ? String(user.trainer.id) : "",
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null); // { type: "success" | "error", text }
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [emailPassword, setEmailPassword] = useState(""); // needed only when the e-mail is changed
  const [pwd, setPwd] = useState({ current_password: "", password: "", password_confirmation: "" });
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdMessage, setPwdMessage] = useState(null);
  const [pwdSaving, setPwdSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePlaceTrainer = (changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setErrors((prev) => ({ ...prev, place_id: undefined, trainer_id: undefined }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    setErrors({});
    setSaving(true);
    try {
      const emailChanged = form.email.trim().toLowerCase() !== (user.email || "").toLowerCase();
      await updateProfile(emailChanged ? { ...form, current_password: emailPassword } : form);
      setEmailPassword("");
      setMessage({ type: "success", text: "Փոփոխությունները պահպանվեցին։" });
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
        setMessage({ type: "error", text: "Ստուգեք լրացված տվյալները։" });
      } else {
        setMessage({ type: "error", text: "Չհաջողվեց պահպանել։ Փորձեք կրկին։" });
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMessage(null);
    setUploading(true);
    try {
      await updatePhoto(file);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.errors?.photo?.[0] || "Չհաջողվեց բեռնել լուսանկարը։",
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePwdChange = (e) => {
    const { name, value } = e.target;
    setPwd((prev) => ({ ...prev, [name]: value }));
    setPwdErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPwdMessage(null);
    setPwdErrors({});
    if (pwd.password !== pwd.password_confirmation) {
      setPwdErrors({ password_confirmation: ["Գաղտնաբառերը չեն համընկնում։"] });
      return;
    }
    setPwdSaving(true);
    try {
      await changePassword(pwd);
      setPwd({ current_password: "", password: "", password_confirmation: "" });
      setPwdMessage({ type: "success", text: "Գաղտնաբառը փոխվեց։" });
    } catch (err) {
      if (err.response?.status === 422) {
        setPwdErrors(err.response.data.errors || {});
      } else {
        setPwdMessage({ type: "error", text: "Չհաջողվեց փոխել գաղտնաբառը։" });
      }
    } finally {
      setPwdSaving(false);
    }
  };

  const fieldClass = (errs, name) => `auth-field${errs[name] ? " invalid" : ""}`;
  const fieldError = (errs, name) =>
    errs[name] && <span className="auth-error">{errs[name][0]}</span>;
  const alertClass = (m) => (m.type === "success" ? "auth-alert success" : "auth-alert");

  return (
    <div className="account-panel">
      <h2 className="account-title">Կարգավորումներ</h2>

      <form className="auth-form" onSubmit={handleSave} noValidate>
        {message && <div className={alertClass(message)}>{message.text}</div>}

        <div className="avatar-upload">
          <img className="avatar-circle" src={user.image_url} alt="avatar" />
          <label className="avatar-btn" htmlFor="settings-photo">
            {uploading ? <Dots /> : "Փոխել լուսանկարը"}
          </label>
          <input id="settings-photo" type="file" accept="image/*" onChange={handlePhoto} disabled={uploading} />
        </div>

        <div className="auth-row">
          <div className={fieldClass(errors, "firstname")}>
            <label htmlFor="firstname">Անուն</label>
            <input id="firstname" name="firstname" value={form.firstname} onChange={handleChange} />
            {fieldError(errors, "firstname")}
          </div>
          <div className={fieldClass(errors, "surname")}>
            <label htmlFor="surname">Ազգանուն</label>
            <input id="surname" name="surname" value={form.surname} onChange={handleChange} />
            {fieldError(errors, "surname")}
          </div>
        </div>

        <div className={fieldClass(errors, "patronymic")}>
          <label htmlFor="patronymic">Հայրանուն</label>
          <input id="patronymic" name="patronymic" value={form.patronymic} onChange={handleChange} />
          {fieldError(errors, "patronymic")}
        </div>

        <div className="auth-row">
          <div className={fieldClass(errors, "date_of_birth")}>
            <label htmlFor="date_of_birth">Ծննդյան ամսաթիվ</label>
            <input id="date_of_birth" name="date_of_birth" type="date" min="1930-01-01" max={TODAY} value={form.date_of_birth} onChange={handleChange} />
            {fieldError(errors, "date_of_birth")}
          </div>
          <div className={fieldClass(errors, "sex")}>
            <label htmlFor="sex">Սեռ</label>
            <select id="sex" name="sex" value={form.sex} onChange={handleChange}>
              <option value="">Ընտրեք սեռը</option>
              <option value="Արական">Արական</option>
              <option value="Իգական">Իգական</option>
            </select>
            {fieldError(errors, "sex")}
          </div>
        </div>

        <div className="auth-row stack">
          <div className={fieldClass(errors, "address")}>
            <label htmlFor="address">Բնակության հասցե</label>
            <input id="address" name="address" value={form.address} onChange={handleChange} />
            {fieldError(errors, "address")}
          </div>
          <div className={fieldClass(errors, "phone")}>
            <label htmlFor="phone">Հեռախոս</label>
            <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} />
            {fieldError(errors, "phone")}
          </div>
        </div>

        <div className={fieldClass(errors, "email")}>
          <label htmlFor="email">Էլ. հասցե</label>
          <input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={handleChange} />
          {fieldError(errors, "email")}
        </div>

        {form.email.trim().toLowerCase() !== (user.email || "").toLowerCase() && (
          <div className={fieldClass(errors, "current_password")}>
            <label htmlFor="email_password">Գաղտնաբառ՝ էլ. հասցեն փոխելու համար</label>
            <PasswordInput
              id="email_password"
              name="email_password"
              autoComplete="current-password"
              canReveal={false}
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
            />
            {fieldError(errors, "current_password")}
          </div>
        )}

        <PlaceTrainerSelect
          placeId={form.place_id}
          trainerId={form.trainer_id}
          onChange={handlePlaceTrainer}
          errors={errors}
        />

        <button className="auth-btn" type="submit" disabled={saving}>
          {saving ? <Dots /> : "Պահպանել"}
        </button>
      </form>

      <TwoFactor />

      <h3 className="account-subtitle">Գաղտնաբառի փոփոխություն</h3>
      <form className="auth-form" onSubmit={handlePasswordSave} noValidate>
        {pwdMessage && <div className={alertClass(pwdMessage)}>{pwdMessage.text}</div>}
        <div className={fieldClass(pwdErrors, "current_password")}>
          <label htmlFor="current_password">Ընթացիկ գաղտնաբառ</label>
          <PasswordInput
            id="current_password"
            name="current_password"
            autoComplete="new-password"
            canReveal={false}
            value={pwd.current_password}
            onChange={handlePwdChange}
          />
          {fieldError(pwdErrors, "current_password")}
        </div>
        <div className="auth-row stack">
          <div className={fieldClass(pwdErrors, "password")}>
            <label htmlFor="new_password">Նոր գաղտնաբառ</label>
            <PasswordInput
              id="new_password"
              name="password"
              autoComplete="new-password"
              value={pwd.password}
              onChange={handlePwdChange}
            />
            {fieldError(pwdErrors, "password")}
          </div>
          <div className={fieldClass(pwdErrors, "password_confirmation")}>
            <label htmlFor="new_password_confirmation">Կրկնել նոր գաղտնաբառը</label>
            <PasswordInput
              id="new_password_confirmation"
              name="password_confirmation"
              autoComplete="new-password"
              value={pwd.password_confirmation}
              onChange={handlePwdChange}
            />
            {fieldError(pwdErrors, "password_confirmation")}
          </div>
        </div>
        <button className="auth-btn outline" type="submit" disabled={pwdSaving}>
          {pwdSaving ? <Dots /> : "Փոխել գաղտնաբառը"}
        </button>
      </form>
    </div>
  );
};

export default Settings;
